// Console ring buffer + crash report hand-off from the error page to the
// support form. The error page does a full reload, so the report travels
// through sessionStorage; the form reads it once and clears it.

const STORAGE_KEY = "support:errorReport";
const MAX_CONSOLE_ENTRIES = 50;
const MAX_ENTRY_LENGTH = 500;
const MAX_DESCRIPTION_LENGTH = 3900;

export interface ConsoleEntry {
  level: "error" | "warn";
  time: string;
  text: string;
}

export interface SupportErrorReport {
  page_path: string;
  error: { name: string; message: string; stack: string };
  console: ConsoleEntry[];
}

const consoleEntries: ConsoleEntry[] = [];
let installed = false;

function stringify(value: unknown): string {
  if (value instanceof Error) return value.stack || `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function pushConsoleEntry(level: ConsoleEntry["level"], args: unknown[]) {
  const text = args.map(stringify).join(" ").slice(0, MAX_ENTRY_LENGTH);
  consoleEntries.push({ level, time: new Date().toISOString(), text });
  if (consoleEntries.length > MAX_CONSOLE_ENTRIES) consoleEntries.shift();
}

export function getConsoleEntries(): ConsoleEntry[] {
  return [...consoleEntries];
}

/** Wrap console.error/warn and window error events once per page load. */
export function installConsoleBuffer() {
  if (installed) return;
  installed = true;
  for (const level of ["error", "warn"] as const) {
    // eslint-disable-next-line no-console -- wrapping, not logging
    const original = console[level];
    // eslint-disable-next-line no-console -- wrapping, not logging
    console[level] = (...args: unknown[]) => {
      pushConsoleEntry(level, args);
      original.apply(console, args);
    };
  }
  window.addEventListener("error", (event) => pushConsoleEntry("error", [event.error ?? event.message]));
  window.addEventListener("unhandledrejection", (event) => pushConsoleEntry("error", ["Unhandled rejection:", event.reason]));
}

export function buildErrorReport(error: Error | null, pagePath: string): SupportErrorReport {
  return {
    page_path: pagePath,
    error: {
      name: error?.name ?? "Error",
      message: error?.message ?? "",
      stack: error?.stack ?? "",
    },
    console: getConsoleEntries(),
  };
}

export function stashErrorReport(error: Error | null, pagePath: string): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buildErrorReport(error, pagePath)));
    return true;
  } catch {
    return false;
  }
}

export function peekErrorReport(): SupportErrorReport | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SupportErrorReport) : null;
  } catch {
    return null;
  }
}

export function clearErrorReport() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage blocked; nothing to clear
  }
}

/** Human-readable description prefill: error, stack head, last console lines. */
export function buildErrorDescription(report: SupportErrorReport): string {
  const stackLines = report.error.stack.split("\n").slice(0, 15).join("\n");
  const consoleLines = report.console.slice(-20).map((entry) => `[${entry.level}] ${entry.text}`).join("\n");
  const parts = [
    `${report.error.name}: ${report.error.message}`.trim(),
    stackLines,
    consoleLines ? `Console:\n${consoleLines}` : "",
  ].filter(Boolean);
  return parts.join("\n\n").slice(0, MAX_DESCRIPTION_LENGTH);
}

export interface ErrorTicketLabels {
  routePattern: string | null;
  pageLabel: string;
  spotLabel: string;
  locale: string;
}

export async function sendErrorReport(
  apiBaseUrl: string,
  report: SupportErrorReport,
  labels: ErrorTicketLabels,
): Promise<number> {
  const formData = new FormData();
  formData.append("page_path", report.page_path);
  formData.append("page_label", labels.pageLabel);
  formData.append("spot_id", "");
  formData.append("spot_label", labels.spotLabel);
  formData.append("description", buildErrorDescription(report));
  formData.append(
    "context",
    JSON.stringify({
      user_agent: navigator.userAgent,
      locale: labels.locale,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      client_time: new Date().toISOString(),
      route_pattern: labels.routePattern,
      error: report.error,
      console: report.console,
    }),
  );
  const response = await fetch(`${apiBaseUrl}/api/support/tickets/`, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers: { "ngrok-skip-browser-warning": "true" },
  });
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) throw { status: response.status, data };
  return (data as { id: number }).id;
}
